import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import useSmallScreen from 'hooks/useSmallScreen';
import Dialog from 'housecall-ui/dist/DesignSystem/Feedback/Dialog/Dialog';
import makeStyles from 'housecall-ui/dist/DesignSystem/Styles/makeStyles';
import useMediaQuery from 'housecall-ui/dist/DesignSystem/Styles/useMediaQuery';
import useTheme from 'housecall-ui/dist/DesignSystem/Styles/useTheme';
import Grow from 'housecall-ui/dist/DesignSystem/Transitions/Transition/Grow';
import { HCPTheme } from 'housecall-ui/dist/Theme/Theme';
import { QueryClientProvider } from 'resources/ReactQuery/QueryClientProvider';
import { isBlank } from 'utils/isBlank';
import { isPresent } from 'utils/isPresent';

import { AppDispatch } from 'components/Common/Data/StoreCreator';
import PayWallAddonsDialog from 'components/PayWall/PayWallAddonsDialog';
import PayWallPaymentReviewDialog from 'components/PayWall/PayWallPaymentReviewDialog';
import PayWallPlanSelectionDialog from 'components/PayWall/PayWallPlanSelectionDialog';
import {
  closePayWall,
  DIALOG_ADD_ONS,
  DIALOG_PAYMENT_REVIEW,
  DIALOG_PLAN_SELECTION,
  DURATION_SELECTED,
  fetchOrganizationAddress,
  navigateToAddonsScreen,
  navigateToReviewPlanChanges,
  PAY_WALL_CONFIRMATION_TEXT,
  PLAN_SELECTED,
  UPDATE_DIALOG_TYPE,
} from 'components/PayWall/Redux/Actions';
import { PayWallAvailablePlans, PayWallFullState, PayWallPlan } from 'components/PayWall/Redux/Reducer.type';
import {
  getAvailablePlansV3,
  getDialogStepFromUrl,
  getPlanTierFromUrl,
  getSelectedDurationFromUrl,
} from 'components/PayWall/Redux/Selectors';
import { identifyWithPlanInterestedIn } from 'components/Services/Analytics/IdentifyPropertiesService';
import { useTrackEventsService } from 'components/Services/Analytics/useTrackEventsService';

// We want to set preferred PayWall dialog height to prevent severe layout shifts when changing steps.
// For smaller devices, we keep height fully dynamic (otherwise we'd encounter overflow and two vertical scrolls).
const PREFERRED_PAYWALL_HEIGHT = 768;

export const CIRCULAR_PROGRESS_BUTTON_SIZE = 24;
export const PAY_WALL_TRANSITION_DURATION = 250;

export const PAY_WALL_VERSION = '1.0.0';

type DialogContentStylesProps = {
  isLoading: boolean;
};

type ButtonProgressStylesProps = {
  circularProgressSize: number;
};

type PayWallDialogStyleProps = {
  isVerticallyHigh: boolean;
};

export const useDialogContentStyles = makeStyles((theme: HCPTheme) => ({
  root: {
    position: 'relative',
    padding: theme.spacing(2, 3),
    opacity: ({ isLoading }: DialogContentStylesProps) => (isLoading ? 0.5 : 1),
    transition: theme.transitions.create('opacity', {
      easing: theme.transitions.easing.easeInOut,
      duration: theme.transitions.duration.standard,
    }),
    minHeight: theme.spacing(3), // accomodate for spinner when content is empty
  },
}));

// Targets incorrect spacing introduced in housecall-ui
export const usePayWallDialogMainGridStyles = makeStyles((theme: HCPTheme) => ({
  root: {
    margin: theme.spacing(-2, -3),
    width: `calc(100% + ${theme.spacing(2 * 3)}px)`,
  },
}));

export const usePayWallDialogGridPaddingFixStyles = makeStyles((theme: HCPTheme) => ({
  item: {
    padding: `${theme.spacing(2)}px ${theme.spacing(3)}px !important`,
  },
}));

export const useButtonProgressStyles = makeStyles(() => ({
  wrapper: {
    position: 'relative',
  },
  buttonProgress: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginTop: ({ circularProgressSize }: ButtonProgressStylesProps) => -circularProgressSize / 2,
    marginLeft: ({ circularProgressSize }: ButtonProgressStylesProps) => -circularProgressSize / 2,
  },
}));

const useStyles = makeStyles({
  scrollPaper: {
    minHeight: ({ isVerticallyHigh }: PayWallDialogStyleProps) =>
      isVerticallyHigh ? `${PREFERRED_PAYWALL_HEIGHT}px` : 'auto',
  },
  dialogPaperIframe: {
    width: '100% !important',
    height: '100% !important',
    margin: 0,
    maxHeight: '100% !important',
    maxWidth: '100% !important',
  },
  toolbar: {
    borderBottom: 'none',
  },
});

type Props = {
  fromIframe: boolean;
  showDialog: boolean;
  dialogType: string;
};

export const useFetchOrganizationAddress = (showDialog: boolean) => {
  const dispatch = useDispatch() as AppDispatch;
  const dispatchFetchOrganizationAddress = useCallback(() => fetchOrganizationAddress(dispatch), [dispatch]);

  useEffect(() => {
    if (showDialog) {
      dispatchFetchOrganizationAddress();
    }
  }, [showDialog, dispatchFetchOrganizationAddress]);
};

export type TransitionComponent = Exclude<React.ComponentProps<typeof Dialog>['TransitionComponent'], undefined>;

export default function PayWallDialog(props: Props): JSX.Element | null {
  const dispatch = useDispatch() as AppDispatch<PayWallFullState>;
  const { trackEvent, trackButtonClicked } = useTrackEventsService();
  const theme = useTheme();
  const isVerticallyHigh = useMediaQuery(`(min-height:${PREFERRED_PAYWALL_HEIGHT + theme.spacing(4) * 2}px)`);
  const classes = useStyles({ isVerticallyHigh });
  const isSmallScreen = useSmallScreen();
  const [navigateFinished, setNavigateFinished] = useState(false);
  const { fromIframe, showDialog, dialogType } = props;

  useFetchOrganizationAddress(showDialog);

  const availableTiers = useSelector(getAvailablePlansV3);
  const originalCurrentPlan = useSelector((state: PayWallFullState) => state.payWall.originalCurrentPlan);
  const planTierFromUrl = useSelector(
    (state: PayWallFullState) => getPlanTierFromUrl(state) as keyof PayWallAvailablePlans
  );
  const selectedDurationFromUrl = useSelector((state: PayWallFullState) => getSelectedDurationFromUrl(state));
  const dialogStepFromUrl = useSelector((state: PayWallFullState) => getDialogStepFromUrl(state));

  const openConfirmDialog = () => dispatch({ type: PAY_WALL_CONFIRMATION_TEXT, status: true });
  const showPayWallDialog = (typeOfDialog: string) => dispatch({ type: UPDATE_DIALOG_TYPE, dialogType: typeOfDialog });

  const updateDurationSelection = (durationSelected: string) =>
    dispatch({ type: DURATION_SELECTED, duration: durationSelected });

  const planSelected = (plan: PayWallPlan) => {
    dispatch(identifyWithPlanInterestedIn(plan.tier));
    trackEvent('Plan selected', {
      plan_tier: plan.tier,
      payWallVersion: PAY_WALL_VERSION,
    });
    dispatch({ type: PLAN_SELECTED, planSelected: plan });
  };

  const updatePlanAndDurationIfNeeded = (plan: PayWallPlan) => {
    if (isPresent(plan)) {
      planSelected(plan);
    }

    if (isPresent(selectedDurationFromUrl)) {
      updateDurationSelection(selectedDurationFromUrl);
    }
  };

  const navigateIfNeeded = (plan: PayWallPlan) => {
    switch (dialogStepFromUrl) {
      case DIALOG_ADD_ONS:
        dispatch(navigateToAddonsScreen(plan.id)).then(() => {
          setNavigateFinished(true);
        });
        break;
      case DIALOG_PAYMENT_REVIEW:
        dispatch(navigateToReviewPlanChanges(plan.id)).then(() => {
          setNavigateFinished(true);
        });
        break;
      default:
        setNavigateFinished(true);
    }
  };

  useEffect(() => {
    if (isBlank(dialogStepFromUrl)) {
      setNavigateFinished(true);
      return;
    }

    const plan = availableTiers.find((tier) => tier.tier === planTierFromUrl);

    if (!plan) {
      return;
    }

    updatePlanAndDurationIfNeeded(plan);
    navigateIfNeeded(plan);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dialogClasses = {
    paperScrollPaper: classes.scrollPaper,
    paper: '',
  };

  if (fromIframe) {
    dialogClasses.paper = classes.dialogPaperIframe;
  }

  if (!navigateFinished) {
    return null;
  }

  const closeDialog = () => {
    trackButtonClicked({ buttonName: 'paywall close button', payWallVersion: PAY_WALL_VERSION });

    if (Number(originalCurrentPlan.rate_flat_monthly) < 1) {
      openConfirmDialog();
      return;
    }

    const { parent } = window;

    dispatch(closePayWall());
    parent.postMessage('closeDialog', '*');
  };

  const dialogContent = {
    [DIALOG_PLAN_SELECTION]: () => <PayWallPlanSelectionDialog onCancel={() => closeDialog()} />,
    [DIALOG_ADD_ONS]: () => (
      <PayWallAddonsDialog
        onCancel={() => closeDialog()}
        onBack={() => {
          showPayWallDialog(DIALOG_PLAN_SELECTION);
          trackButtonClicked({
            buttonName: 'paywall back button',
            payWallVersion: PAY_WALL_VERSION,
            source: DIALOG_ADD_ONS,
          });
        }}
      />
    ),
    [DIALOG_PAYMENT_REVIEW]: () => {
      const onReviewGoBack = () => {
        showPayWallDialog(DIALOG_ADD_ONS);
        trackButtonClicked({
          buttonName: 'paywall back button',
          payWallVersion: PAY_WALL_VERSION,
          source: DIALOG_PAYMENT_REVIEW,
        });
      };

      return <PayWallPaymentReviewDialog onCancel={() => closeDialog()} onBack={onReviewGoBack} />;
    },
  }[dialogType];

  if (!dialogContent) {
    return null;
  }

  return (
    <QueryClientProvider>
      <Dialog
        maxWidth="lg"
        transitionDuration={!fromIframe ? PAY_WALL_TRANSITION_DURATION : 0}
        TransitionComponent={Grow as TransitionComponent}
        fullWidth={!isSmallScreen}
        fullScreen={isSmallScreen}
        open={showDialog}
        classes={dialogClasses}
      >
        {dialogContent()}
      </Dialog>
    </QueryClientProvider>
  );
}
